import React from 'react';
import { Dropdown, Popconfirm } from 'antd';
import { TeamOutlined, MoreOutlined } from '@ant-design/icons';
import {
    Table,
    Text,
    Pill,
    TableVariants,
    Avatar,
} from '@clockout-dev/design-elements';
import SectionCard from '../../common/sectionCard/SectionCard';
import UserStatusBadge from '../../common/userStatusBadge/UserStatusBadge';
import styles from '../styles/team.module.scss';

/**
 * `roleOptions` comes from the FI's own dynamic role catalog (see
 * team.controller.js#getAssignableRoles) — never a hardcoded role list,
 * since an FI can rename/create/delete its roles from the Role Composer.
 */
function ActiveTeamCard({
    users = [],
    loading,
    pageInfo,
    canChangeRole = false,
    // Each destructive row action carries its own capability flag. They
    // default to false so a caller that forgets to pass one renders a
    // read-only menu rather than a working revoke button.
    canResendInvitation = false,
    canResetPassword = false,
    canRemoveAccess = false,
    roleOptions = [],
    onTableChange,
    getRole,
    getStatus,
    formatLastActivity,
    onResetPassword,
    onRemoveAccess,
    onChangeRole,
    onResendInvitation,
}) {
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (_, user) => <Avatar name={user.name || user.email} subtitle={user.email} size="small" />,
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (_, user) => (
                <Pill
                    size="small"
                    text={(getRole(user) || '').toUpperCase()}
                    textSize="xs"
                    classNames={{ root: styles.rolePill, text: styles.rolePillText }}
                />
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, user) => <UserStatusBadge status={getStatus(user)} />,
        },
        {
            title: 'Last Activity',
            dataIndex: 'lastActivity',
            key: 'lastActivity',
            render: (_, user) => (
                <Text
                    as="span"
                    size="s"
                    className={styles.lastActivity}>
                    {formatLastActivity(user)}
                </Text>
            ),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, user) => {
                const email = user.email;
                const userId = user.id;
                const memberName = user.name || email || 'this team member';
                const currentRoleId = user.roleId;

                // Check if user has not accepted invitation yet
                const isInvitedPending = user.invited && !user.active;

                // TODO: replace Popconfirm with design-elements ConfirmModal when available in @clockout-dev/design-elements.
                // The trigger span must cover the item's full row
                // (menuConfirmTrigger), otherwise clicks landing outside the
                // label text bypass the Popconfirm and silently no-op. Its
                // stopPropagation keeps the click from reaching rc-menu,
                // which would close the dropdown out from under the popup.
                const confirmLabel = (text, className, confirmProps) => (
                    <Popconfirm
                        placement="topRight"
                        cancelText="Cancel"
                        {...confirmProps}
                    >
                        <span
                            className={className ? `${styles.menuConfirmTrigger} ${className}` : styles.menuConfirmTrigger}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {text}
                        </span>
                    </Popconfirm>
                );

                const resetPasswordDisabled = !email || isInvitedPending;
                const accessControlDisabled = !userId || isInvitedPending;

                // Disabled items keep receiving pointer events, so they get a
                // plain-text label — a mounted Popconfirm would still open
                // from a "disabled" row.
                const menuItems = [
                    ...(canChangeRole && userId && !isInvitedPending ? [
                        {
                            key: 'change-role',
                            label: 'Change role',
                            children: roleOptions.map((option) => ({
                                key: `change-role-${option.value}`,
                                disabled: option.value === currentRoleId,
                                label: option.value === currentRoleId ? option.label : confirmLabel(option.label, null, {
                                    title: `Change role to ${option.label}?`,
                                    description: `${memberName} will be set to ${option.label}.`,
                                    okText: 'Change',
                                    onConfirm: () => onChangeRole?.(userId, option.value),
                                }),
                            })),
                        }
                    ] : []),
                    ...(isInvitedPending ? [
                        ...(canResendInvitation ? [{
                            key: 'resend-invitation',
                            label: confirmLabel('Resend invitation', null, {
                                title: 'Resend invitation?',
                                description: `Invitation will be resent to ${email}.`,
                                okText: 'Resend',
                                onConfirm: () => onResendInvitation?.(email, currentRoleId),
                            }),
                        }] : []),
                    ] : [
                        ...(canResetPassword ? [{
                            key: 'reset-password',
                            label: resetPasswordDisabled ? 'Reset password' : confirmLabel('Reset password', null, {
                                title: 'Send password reset email?',
                                description: `A password reset email will be sent to ${email}.`,
                                okText: 'Send',
                                onConfirm: () => onResetPassword?.(email),
                            }),
                            disabled: resetPasswordDisabled,
                            title: isInvitedPending ? 'User must accept invitation first' : '',
                        }] : []),
                        ...(canRemoveAccess ? [{
                            key: 'access-control',
                            label: accessControlDisabled
                                ? (user.active ? 'Remove access' : 'Grant access')
                                : confirmLabel(
                                    user.active ? 'Remove access' : 'Grant access',
                                    user.active ? styles.removeAccessText : styles.grantAccessText,
                                    {
                                        title: user.active ? "Remove this user's access?" : "Grant access to this user?",
                                        description: user.active
                                            ? `${memberName} will no longer be able to access this FI.`
                                            : `${memberName} will be able to access this FI.`,
                                        okText: user.active ? 'Remove' : 'Grant',
                                        okButtonProps: { danger: user.active },
                                        onConfirm: () => onRemoveAccess?.(userId, user.active),
                                    },
                                ),
                            danger: user.active,
                            disabled: accessControlDisabled,
                        }] : []),
                    ]),
                ];

                // A session with no action scopes gets no trigger at all,
                // rather than a button that opens an empty menu.
                if (!menuItems.length) return null;

                return (
                    <div className={styles.actionsCell}>
                        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                            <button
                                type="button"
                                className={styles.actionsButton}
                                aria-label={`Actions for ${user.name || email || 'team member'}`}
                            >
                                <MoreOutlined />
                            </button>
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    return (
        <SectionCard icon={<TeamOutlined />} title="Active Team">
            <Table
                variant={TableVariants.HISTORY}
                columns={columns}
                dataSource={users}
                pageInfo={pageInfo || null}
                onChange={onTableChange}
                loading={loading}
                saveSearchParams
                rowKey={(user) => user.id ?? user.email}
                emptyDescription="No team members yet."
                classNames={{ shell: styles.teamTableShell, root: styles.teamTableRoot }}
            />
        </SectionCard>
    );
}

export default ActiveTeamCard;
